import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Home, CreditCard, Plus, User, Settings, LogOut, Menu, X, LayoutDashboard, Users, Receipt, FileText, Palette, Wrench, ScrollText, MessageCircle, Wallet, CreditCard as CardIcon, Send } from "lucide-react";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";

const HR_DAYS = ["Ned", "Pon", "Uto", "Sri", "Čet", "Pet", "Sub"];
const HR_MONTHS = ["siječnja", "veljače", "ožujka", "travnja", "svibnja", "lipnja", "srpnja", "kolovoza", "rujna", "listopada", "studenoga", "prosinca"];

function ClockStatus() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const dateStr = `${HR_DAYS[now.getDay()]}, ${String(now.getDate()).padStart(2, "0")}. ${HR_MONTHS[now.getMonth()]} ${now.getFullYear()}`;
  const timeStr = now.toLocaleTimeString("hr-HR", { hour12: false });
  return (
    <div className="mt-4 px-4 py-3 rounded-xl bg-white/5 border border-white/10" data-testid="sidebar-clock">
      <div className="flex items-center gap-2">
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-70 animate-ping"></span>
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
        </span>
        <span className="text-green-400 text-[11px] font-bold uppercase tracking-widest" data-testid="sidebar-active-label">Aktivan</span>
      </div>
      <div className="text-white/80 text-xs mt-2" data-testid="sidebar-date">{dateStr}</div>
      <div className="text-[#FFD60A] font-heading font-bold text-lg tabular-nums tracking-wide" data-testid="sidebar-time">{timeStr}</div>
    </div>
  );
}

function SupportContact({ onNavigate }) {
  const { branding } = useAuth();
  const num = (branding?.support_whatsapp || "").replace(/[^0-9]/g, "");
  if (!num || branding?.support_whatsapp_enabled === false) return null;
  const logo = branding?.support_logo_url
    ? (branding.support_logo_url.startsWith("http") ? branding.support_logo_url : `${process.env.REACT_APP_BACKEND_URL}${branding.support_logo_url}`)
    : null;
  return (
    <a
      href={`https://wa.me/${num}`}
      target="_blank"
      rel="noreferrer"
      onClick={onNavigate}
      data-testid="support-whatsapp-link"
      className="mt-6 flex items-center gap-3 px-4 py-3 rounded-xl border border-[#FFD60A]/30 bg-[#FFD60A]/5 hover:bg-[#FFD60A]/15 transition-colors"
    >
      {logo ? (
        <img src={logo} alt="Podrška" className="w-10 h-10 rounded-full object-contain bg-black/40" />
      ) : (
        <MessageCircle size={20} className="text-[#FFD60A]" />
      )}
      <span className="text-[#FFD60A] text-xs font-heading font-bold uppercase tracking-widest leading-tight">Kontakt<br/>WhatsApp</span>
    </a>
  );
}

function TikTokContact() {
  const { branding } = useAuth();
  const [open, setOpen] = useState(false);
  const handle = (branding?.support_tiktok || "").replace(/^@/, "").trim();
  if (!handle || branding?.support_tiktok_enabled === false) return null;
  const qr = branding?.tiktok_qr_url
    ? (branding.tiktok_qr_url.startsWith("http") ? branding.tiktok_qr_url : `${process.env.REACT_APP_BACKEND_URL}${branding.tiktok_qr_url}`)
    : null;
  const profileUrl = `https://www.tiktok.com/@${handle}`;
  return (
    <>
      <button
        type="button"
        onClick={() => (qr ? setOpen(true) : window.open(profileUrl, "_blank"))}
        data-testid="support-tiktok-link"
        className="mt-3 flex items-center gap-3 px-4 py-3 rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 transition-colors text-left"
      >
        <svg viewBox="0 0 24 24" className="w-6 h-6 shrink-0" fill="currentColor" aria-hidden="true">
          <path d="M16.6 5.82A5.2 5.2 0 0 1 15.4 2h-3.1v13.1a2.6 2.6 0 1 1-1.86-2.5V9.4a5.7 5.7 0 1 0 4.96 5.64V8.3a8.3 8.3 0 0 0 4.6 1.4V6.6a5.2 5.2 0 0 1-3.4-.78Z"/>
        </svg>
        <span className="text-xs font-heading font-bold uppercase tracking-widest leading-tight">
          TikTok<br/><span className="text-[10px] tracking-normal normal-case font-medium opacity-70">@{handle}</span>
        </span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#171717] border-white/10 text-white max-w-xs" data-testid="tiktok-qr-dialog">
          <DialogHeader><DialogTitle>TikTok @{handle}</DialogTitle></DialogHeader>
          {qr && <img src={qr} alt="TikTok kod" className="w-full rounded-xl bg-white p-2" data-testid="tiktok-qr-image" />}
          <a href={profileUrl} target="_blank" rel="noreferrer" className="btn-yellow rounded-full h-11 inline-flex items-center justify-center mt-2" data-testid="tiktok-open-profile">Otvori TikTok profil</a>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Sidebar({ items, testIdPrefix }) {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    const poll = async () => { try { const { data } = await api.get("/messages/unread-count"); setUnread(data.unread || 0); } catch {} };
    poll();
    const t = setInterval(poll, 10000);
    const ping = () => { api.post("/presence/ping").catch(() => {}); };
    ping();
    const p = setInterval(ping, 45000);
    return () => { clearInterval(t); clearInterval(p); };
  }, []);

  const doLogout = () => { logout(); nav("/"); };

  const Nav = (
    <nav className="flex flex-col gap-1 mt-8">
      {items.map((it) => (
        <NavLink key={it.to} to={it.to} end={it.end} onClick={() => setOpen(false)}
          data-testid={`${testIdPrefix}-nav-${it.key}`}
          className={({ isActive }) => cn(
            "flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors",
            isActive ? "bg-[#FFD60A] text-black" : "text-white/70 hover:text-white hover:bg-white/5"
          )}>
          <it.icon size={18} />
          <span className="flex-1">{it.label}</span>
          {it.key === "messages" && unread > 0 && (
            <span className={cn("min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold flex items-center justify-center", "bg-red-500 text-white")} data-testid="nav-unread-badge">{unread > 99 ? "99+" : unread}</span>
          )}
        </NavLink>
      ))}
      <button onClick={doLogout} data-testid={`${testIdPrefix}-logout`}
        className="mt-6 flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-white/60 hover:text-white hover:bg-white/5 transition-colors">
        <LogOut size={18} /> Odjava
      </button>
      <SupportContact onNavigate={() => setOpen(false)} />
      <TikTokContact />
      <ThemeToggle className="mt-4 self-start" />
    </nav>
  );

  return (
    <>
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 flex items-center justify-between px-4 py-3 bg-[#0a0a0a] border-b border-white/10">
        <Logo size="lg" light />
        <button onClick={() => setOpen(true)} className="text-white p-2" data-testid={`${testIdPrefix}-menu-open`} aria-label="Open menu">
          <Menu size={24} />
        </button>
      </div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 bg-[#0a0a0a] p-6 overflow-y-auto">
          <div className="flex justify-between items-center">
            <Logo size="lg" light />
            <button onClick={() => setOpen(false)} className="text-white p-2" data-testid={`${testIdPrefix}-menu-close`} aria-label="Close menu">
              <X size={24} />
            </button>
          </div>
          <ClockStatus />
          {Nav}
        </div>
      )}
      <aside className="hidden lg:flex flex-col w-64 bg-[#0a0a0a] border-r border-white/10 min-h-screen p-6 sticky top-0">
        <Logo size="lg" light />
        <ClockStatus />
        {Nav}
      </aside>
    </>
  );
}

export function UserSidebar() {
  return <Sidebar testIdPrefix="user" items={[
    { key: "home", to: "/dashboard", label: "Početna", icon: Home, end: true },
    { key: "wallet", to: "/wallet", label: "Novčanik", icon: Wallet },
    { key: "cards", to: "/cards", label: "Virtualne kartice", icon: CardIcon },
    { key: "payments", to: "/payments", label: "Moja plaćanja", icon: CreditCard },
    { key: "new", to: "/payments/new", label: "Nova uplata", icon: Plus },
    { key: "messages", to: "/messages", label: "Poruke", icon: MessageCircle },
    { key: "profile", to: "/profile", label: "Profil", icon: User },
    { key: "settings", to: "/settings", label: "Postavke", icon: Settings },
  ]}/>;
}

export function AdminSidebar() {
  return <Sidebar testIdPrefix="admin" items={[
    { key: "dashboard", to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { key: "users", to: "/admin/users", label: "Korisnici", icon: Users },
    { key: "payments", to: "/admin/payments", label: "Uplate", icon: Receipt },
    { key: "requests", to: "/admin/payment-requests", label: "Zahtjevi", icon: FileText },
    { key: "cards", to: "/admin/cards", label: "Kartice", icon: CardIcon },
    { key: "payouts", to: "/admin/payouts", label: "Isplate", icon: Send },
    { key: "messages", to: "/admin/messages", label: "Poruke", icon: MessageCircle },
    { key: "stripe", to: "/admin/stripe", label: "Stripe", icon: CreditCard },
    { key: "branding", to: "/admin/branding", label: "Branding", icon: Palette },
    { key: "settings", to: "/admin/settings", label: "Postavke", icon: Wrench },
    { key: "audit", to: "/admin/audit-log", label: "Audit Log", icon: ScrollText },
  ]}/>;
}
